import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatTarih } from '../../lib/api';
import {
  Alan, GORSEL_KABUL, SayfaBaslik, SiteGorsel, Sonuc, boyutYaz, medyaYukle, useSiteDurum, useYuklemeAlani, type Medya,
} from './ortak';

const duz = (s: string) => s.toLocaleLowerCase('tr');

/** Yan çekmece: seçilen görselin ayrıntıları — alt metin, kopyalanabilir adres, kullanıldığı yerler, sil. */
function Cekmece({ m, kapat, degisti, silindi }: { m: Medya; kapat: () => void; degisti: (m: Medya) => void; silindi: () => void }) {
  const [alt, setAlt] = useState(m.alt);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  useEffect(() => { setAlt(m.alt); }, [m.id, m.alt]);
  useEffect(() => { setBilgi(null); setHata(null); }, [m.id]);
  useEffect(() => {
    const tus = (e: KeyboardEvent) => { if (e.key === 'Escape') kapat(); };
    document.addEventListener('keydown', tus);
    return () => document.removeEventListener('keydown', tus);
  }, [kapat]);

  async function altKaydet() {
    setHata(null); setBilgi(null); setMesgul(true);
    try {
      const r = await api<{ gorsel: Medya }>(`/api/site/medya/${m.id}`, { method: 'PATCH', body: JSON.stringify({ alt: alt.trim() }) });
      degisti({ ...m, alt: r.gorsel.alt }); setBilgi('Alt metin kaydedildi');
    } catch (e) { setHata(e instanceof Error ? e.message : 'Kaydedilemedi'); }
    finally { setMesgul(false); }
  }
  async function kopyala() {
    try { await navigator.clipboard.writeText(m.yol); setBilgi('Adres kopyalandı'); }
    catch { setBilgi('Adresi kutudan seçip kopyalayın'); }
  }
  async function sil() {
    const kullaniliyor = m.kullanim.length > 0;
    const soru = kullaniliyor
      ? `“${m.dosyaAdi}” şu an KULLANILIYOR:\n\n  · ${m.kullanim.join('\n  · ')}\n\nSilerseniz bu yerlerde görseli yeniden seçmeden yayın yapılamaz. Yine de silinsin mi?`
      : `“${m.dosyaAdi}” kütüphaneden silinsin mi? Bu işlem geri alınamaz.`;
    if (!window.confirm(soru)) return;
    setHata(null); setBilgi(null);
    try { await api(`/api/site/medya/${m.id}${kullaniliyor ? '?zorla=1' : ''}`, { method: 'DELETE' }); silindi(); }
    catch (e) {
      // Liste bayatsa (görsel bu arada bir yere eklendiyse) sunucu kullanım yerini söyler.
      setHata(e instanceof ApiError && e.status === 409 ? `${e.message}. Sayfayı yenileyip yeniden deneyin.` : e instanceof Error ? e.message : 'Silinemedi');
    }
  }

  return (
    <div className="ccd-drawer-arka" onMouseDown={(e) => { if (e.target === e.currentTarget) kapat(); }}>
      <aside className="ccd-drawer ccd-site-cekmece ccd-stack" role="dialog" aria-modal="true" aria-label="Görsel ayrıntıları">
        <div className="ccd-row-between">
          <h2 className="ccd-site-kart-baslik ccd-truncate" title={m.dosyaAdi}>{m.dosyaAdi}</h2>
          <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
        </div>
        <SiteGorsel yol={m.yol} alt={m.alt} className="ccd-site-cekmece-gorsel" />
        <div className="ccd-meta">{m.en && m.boy ? `${m.en} × ${m.boy} · ` : ''}{boyutYaz(m.boyut)} · {formatTarih(m.eklendi)}</div>
        <Sonuc bilgi={bilgi} hata={hata} />

        <Alan id="gm-alt" etiket="Alt metin" sayac={`${alt.length} / 200`} ipucu="Görselde ne var? Ekran okuyucular ve arama motorları okur.">
          <div className="ccd-site-yanyana">
            <input id="gm-alt" className="ccd-input" maxLength={200} value={alt} onChange={(e) => setAlt(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter' && alt.trim() !== m.alt) void altKaydet(); }} />
            <button type="button" className="ccd-btn" disabled={mesgul || alt.trim() === m.alt} onClick={() => void altKaydet()}>Kaydet</button>
          </div>
        </Alan>
        <Alan id="gm-yol" etiket="Adres" ipucu="Blog yazısına elle eklemek için.">
          <div className="ccd-site-yanyana">
            <input id="gm-yol" className="ccd-input ccd-mono ccd-input-readonly" readOnly value={m.yol} onFocus={(e) => e.target.select()} />
            <button type="button" className="ccd-btn" onClick={() => void kopyala()}>Kopyala</button>
          </div>
        </Alan>
        <div className="ccd-field">
          <span className="ccd-field-label">Kullanıldığı yer</span>
          {m.kullanim.length === 0 ? <div className="ccd-meta">Hiçbir yerde kullanılmıyor.</div>
            : <ul className="ccd-site-degisiklik">{m.kullanim.map((k, i) => <li key={i}>{k}</li>)}</ul>}
        </div>
        <div className="ccd-site-cekmece-alt">
          <button type="button" className="ccd-btn ccd-btn-danger" onClick={() => void sil()}>Görseli sil</button>
        </div>
      </aside>
    </div>
  );
}

/** Site › Görseller — dosya kütüphanesi: ızgara, arama, kullanım rozeti; tıklayınca yan çekmece. */
export default function SiteGorseller() {
  const { durum, yenile } = useSiteDurum();
  const [liste, setListe] = useState<Medya[] | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [ara, setAra] = useState('');
  const [sekme, setSekme] = useState<'hepsi' | 'kullanilan' | 'bos'>('hepsi');
  const [secili, setSecili] = useState<number | null>(null);

  const getir = useCallback(
    () => api<{ gorseller: Medya[] }>('/api/site/medya').then((r) => setListe(r.gorseller)).catch((e) => setHata((e as Error).message)),
    [],
  );
  useEffect(() => { void getir(); }, [getir]);

  const yukle = useCallback(async (dosyalar: File[]) => {
    if (!dosyalar.length) return;
    setHata(null); setBilgi(null);
    try {
      const eklenen = await medyaYukle(dosyalar, (s, t) => setMesgul(`${s} / ${t} yükleniyor…`));
      setBilgi(eklenen.length === 1 ? 'Görsel eklendi' : `${eklenen.length} görsel eklendi`);
    } catch (e) { setHata(e instanceof Error ? e.message : 'Görsel yüklenemedi'); }
    finally { setMesgul(null); await getir(); }
  }, [getir]);
  const { uzerinde, alan } = useYuklemeAlani((fs) => void yukle(fs), secili === null);

  const q = duz(ara.trim());
  const aranan = (liste ?? []).filter((m) => !q || duz(`${m.dosyaAdi} ${m.alt} ${m.kullanim.join(' ')}`).includes(q));
  const uyar = (m: Medya, s: typeof sekme) => s === 'hepsi' || (s === 'kullanilan' ? m.kullanim.length > 0 : m.kullanim.length === 0);
  const gorunen = aranan.filter((m) => uyar(m, sekme));
  const acik = liste?.find((m) => m.id === secili) ?? null;

  return (
    <div className={`ccd-stack${uzerinde ? ' ccd-site-uzerinde' : ''}`} {...alan}>
      <SayfaBaslik baslik="Görseller" durum={durum}>
        <label className="ccd-btn ccd-btn-primary" style={{ cursor: mesgul ? 'default' : 'pointer' }}>
          {mesgul ?? 'Görsel yükle'}
          <input type="file" accept={GORSEL_KABUL} multiple hidden disabled={!!mesgul}
                 onChange={(e) => { const fs = [...(e.target.files ?? [])]; e.target.value = ''; void yukle(fs); }} />
        </label>
      </SayfaBaslik>
      <Sonuc bilgi={bilgi} hata={hata} />

      <div className="ccd-card ccd-card-flush ccd-site-dizin-kart">
        <div className="ccd-site-arac">
          <div className="ccd-tabs" role="tablist">
            {([['hepsi', 'Tümü'], ['kullanilan', 'Kullanılan'], ['bos', 'Kullanılmayan']] as const).map(([id, ad]) => (
              <button key={id} role="tab" aria-selected={sekme === id} className={`ccd-tab${sekme === id ? ' ccd-is-active' : ''}`} onClick={() => setSekme(id)}>
                {ad}{liste ? <span className="ccd-site-adet">{aranan.filter((m) => uyar(m, id)).length}</span> : null}
              </button>
            ))}
          </div>
          <div className="ccd-site-suz">
            <input className="ccd-input" type="search" placeholder="Dosya adı, alt metin ya da kullanıldığı yer" aria-label="Görsellerde ara" value={ara} onChange={(e) => setAra(e.target.value)} />
          </div>
        </div>
        {!liste ? (!hata && <div className="ccd-site-bosluk ccd-inline-note">Yükleniyor…</div>) : gorunen.length === 0 ? (
          <div className="ccd-site-bosluk">
            <div className="ccd-site-kart-baslik">{liste.length === 0 ? 'Kütüphane boş' : 'Bu süzgeçte görsel yok'}</div>
            {liste.length === 0 && <div className="ccd-meta">Yükleyin, sürükleyin ya da Ctrl+V ile yapıştırın.</div>}
          </div>
        ) : (
          <div className="ccd-site-dosyalar">
            {gorunen.map((m) => (
              <button type="button" key={m.id} className={`ccd-site-dosya${secili === m.id ? ' ccd-is-active' : ''}`} onClick={() => setSecili(m.id)}>
                <SiteGorsel yol={m.yol} alt={m.alt} className="ccd-site-dosya-gorsel" />
                <span className="ccd-site-dosya-ad ccd-truncate" title={m.dosyaAdi}>{m.dosyaAdi}</span>
                <span className={`ccd-badge${m.kullanim.length ? ' ccd-badge-success' : ''}`} title={m.kullanim.join(' · ') || undefined}>
                  {m.kullanim.length ? `${m.kullanim.length} yerde` : 'Kullanılmıyor'}
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="ccd-site-arac-alt ccd-field-hint">JPG, PNG ya da WEBP · en çok 8 MB · sürükle-bırak ve Ctrl+V çalışır.</div>
      </div>

      {acik && (
        <Cekmece m={acik} kapat={() => setSecili(null)}
                 degisti={(y) => setListe((l) => (l ? l.map((x) => (x.id === y.id ? y : x)) : l))}
                 silindi={() => { setSecili(null); setBilgi('Görsel silindi'); void getir(); void yenile(); }} />
      )}
    </div>
  );
}
